import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  CircularProgress,
  Link as MuiLink,
  TextField,
  Grid2,
  Stack,
} from "@mui/material";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import config from "../../../common/Config";
import { getRhpInvoicePrintUrl, shouldUseRhpInvoicePrint } from "../../Epos/utils/rhpInvoicePrint";
import CommonDatePicker from "../../../datepicker/CommonDatePicker";
import { format } from "date-fns";

const getChannelByRole = () => {
  try {
    const activeRoleId = localStorage.getItem("active_role_id");
    const userRolesData = localStorage.getItem("user_roles_data");
    if (activeRoleId && userRolesData) {
      const roles = JSON.parse(userRolesData);
      const activeRole = roles.find(
        (role) => String(role.id) === String(activeRoleId)
      );
      if (activeRole) {
        if (
          activeRole.name === "Tele Calling" ||
          String(activeRole.id) === "20"
        ) {
          return 5;
        }
        if (activeRole.name === "Till" || String(activeRole.id) === "18") {
          return 4;
        }
        const roleName = String(activeRole.name || "").toLowerCase();
        if (String(activeRole.id) === "22" || roleName.includes("account")) {
          return "";
        }
      }
    }
    if (String(activeRoleId) === "20") return 5;
    if (String(activeRoleId) === "18") return 4;
    if (String(activeRoleId) === "22") return "";
    return 5;
  } catch (e) {
    return 5;
  }
};

const formatMoney = (value) => {
  const num = Number(String(value ?? "0").replace(/,/g, ""));
  if (Number.isNaN(num)) return String(value ?? "-");
  return num.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getInvoiceUrl = (row) => {
  if (shouldUseRhpInvoicePrint()) {
    return getRhpInvoicePrintUrl(row.ids, {
      autoprint: true,
      statusTab: row.sale_status || "",
    });
  }
  return `${config.BASE_URL}/sales/invoice/${row.ids}`;
};

const CustomerOrderHistoryPopup = ({
  open,
  onClose,
  customer,
}) => {
  const accessToken = localStorage.getItem("accessToken");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [statusTab, setStatusTab] = useState("Delivered");
  const [dates, setDates] = useState([null, null]);
  const [orderId, setOrderId] = useState("");

  const customerId = customer?.id || "";
  const accountNo = String(
    customer?.accound_no ||
      customer?.account_no ||
      customer?.accountNo ||
      customer?.account_number ||
      ""
  ).trim();
  const customerName = customer?.company || customer?.name || "";

  useEffect(() => {
    if (!open || (!customerId && !accountNo)) {
      setRows([]);
      return;
    }
    let active = true;
    setLoading(true);
    const startDate = dates[0] ? format(dates[0], "yyyy-MM-dd") : "";
    const endDate = dates[1] ? format(dates[1], "yyyy-MM-dd") : "";
    const params = new URLSearchParams();
    params.append("draw", "1");
    params.append("start", "0");
    params.append("length", "100");
    params.append("searchValue", accountNo || customerName);
    params.append("sale_status", statusTab);
    params.append("channels", String(getChannelByRole()));
    params.append("search[value]", accountNo || customerName);
    params.append("search[regex]", "false");
    params.append("customer_id", String(customerId || ""));
    params.append("account_no", accountNo);
    params.append("searchByFromdate", startDate);
    params.append("searchByTodate", endDate);
    axios
      .post(`${config.API_URL}/sales/saleslistv1`, params, {
        headers: {
          authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
      })
      .then((response) => {
        if (!active) return;
        const json = response.data || {};
        setRows(Array.isArray(json.data) ? json.data : []);
      })
      .catch(() => {
        if (!active) return;
        setRows([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, customerId, accountNo, customerName, statusTab, accessToken, dates]);

  const orderIdValue = orderId.trim().toLowerCase();
  const visibleRows = orderIdValue
    ? rows.filter((row) =>
        String(row.reference_no || "")
          .toLowerCase()
          .includes(orderIdValue)
      )
    : rows;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        Order History{customerName ? ` - ${customerName}` : ""}
        <IconButton onClick={onClose} size="small">
          <Iconify icon="mdi:close" width={20} height={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid2 container spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <Grid2 size={{ xs: 12, md: 5 }}>
            <CommonDatePicker
              className="input-border"
              value={dates}
              onChange={setDates}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Invoice No"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={statusTab === "New" ? "contained" : "outlined"}
                onClick={() => setStatusTab("New")}
                className={statusTab === "New" ? "button-variant-contained" : "button-variant-outlined"}
              >
                New
              </Button>
              <Button
                size="small"
                variant={statusTab === "Delivered" ? "contained" : "outlined"}
                onClick={() => setStatusTab("Delivered")}
                className={
                  statusTab === "Delivered"
                    ? "button-variant-contained"
                    : "button-variant-outlined"
                }
              >
                Delivered
              </Button>
            </Stack>
          </Grid2>
        </Grid2>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "14%" }}>Order Date</TableCell>
                <TableCell sx={{ width: "14%" }}>Invoice No</TableCell>
                <TableCell sx={{ width: "30%" }}>Customers (A/C No)</TableCell>
                <TableCell align="right" sx={{ width: "14%" }}>Order Amt</TableCell>
                <TableCell sx={{ width: "14%" }}>Order By</TableCell>
                <TableCell align="center" sx={{ width: "14%" }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                    <TableCell colSpan={6} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                visibleRows.map((row) => (
                  <TableRow key={row.ids || row.reference_no} hover>
                    <TableCell>{row.date || "-"}</TableCell>
                    <TableCell>
                      <MuiLink
                        href={getInvoiceUrl(row)}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="always"
                        sx={{
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          color: "#212b36",
                          textDecoration: "underline !important",
                        }}
                      >
                        {row.reference_no || "-"}
                      </MuiLink>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>{row.customer || customerName}</TableCell>
                    <TableCell align="right">{formatMoney(row.grand_total)}</TableCell>
                    <TableCell>{row.name || row.order_by || row.orderBy || "-"}</TableCell>
                    <TableCell align="center">{row.sale_status || "-"}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerOrderHistoryPopup;
